import { assertSafeInteger, divCeil, divRoundHalfUp, percentToBasisPoints } from './arithmetic.js';

export type Currency = 'UAH';

/** An amount of money in integer minor units (kopecks). Never a float. */
export interface Money {
  readonly kopecks: number;
  readonly currency: Currency;
}

const BASIS_POINTS = 10_000;

export function money(kopecks: number, currency: Currency = 'UAH'): Money {
  assertSafeInteger(kopecks, 'kopecks');
  return Object.freeze({ kopecks: kopecks || 0, currency });
}

export const zero = (currency: Currency = 'UAH'): Money => money(0, currency);

function sameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new TypeError(`currency mismatch: ${a.currency} vs ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.kopecks + b.kopecks, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.kopecks - b.kopecks, a.currency);
}

export function sum(amounts: readonly Money[], currency: Currency = 'UAH'): Money {
  return amounts.reduce(add, zero(currency));
}

export function negate(a: Money): Money {
  return money(-a.kopecks, a.currency);
}

/** Unit price × quantity (quantity must be an integer). */
export function multiply(a: Money, quantity: number): Money {
  assertSafeInteger(quantity, 'quantity');
  return money(a.kopecks * quantity, a.currency);
}

/** `percent`% of an amount, rounded half-up to the kopeck. */
export function percentOf(a: Money, percent: number): Money {
  const bp = percentToBasisPoints(percent);
  return money(divRoundHalfUp(a.kopecks * bp, BASIS_POINTS), a.currency);
}

/** Amount reduced by `percent`% (e.g. a 15% discount), rounded half-up to the kopeck. */
export function discountByPercent(a: Money, percent: number): Money {
  const bp = percentToBasisPoints(percent);
  return money(divRoundHalfUp(a.kopecks * (BASIS_POINTS - bp), BASIS_POINTS), a.currency);
}

/** Amount increased by `percent`% (e.g. markup on cost), rounded half-up to the kopeck. */
export function markupByPercent(a: Money, percent: number): Money {
  const bp = percentToBasisPoints(percent);
  return money(divRoundHalfUp(a.kopecks * (BASIS_POINTS + bp), BASIS_POINTS), a.currency);
}

/**
 * Q-points rounding (spec §5): every Q-points calculation rounds UP.
 * points = ceil(amount × accrualPercent / 100 / pointValue). With 10% and 1 point = 1 UAH,
 * 95 UAH → 10 points.
 */
export function ceilPoints(amount: Money, accrualPercent: number, pointValue: Money): number {
  sameCurrency(amount, pointValue);
  if (amount.kopecks < 0) throw new RangeError('amount must not be negative');
  if (pointValue.kopecks <= 0) throw new RangeError('pointValue must be positive');
  const bp = percentToBasisPoints(accrualPercent);
  return divCeil(amount.kopecks * bp, BASIS_POINTS * pointValue.kopecks);
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b);
  return a.kopecks === b.kopecks ? 0 : a.kopecks < b.kopecks ? -1 : 1;
}

export const equals = (a: Money, b: Money): boolean => compare(a, b) === 0;
export const isZero = (a: Money): boolean => a.kopecks === 0;
export const min = (a: Money, b: Money): Money => (compare(a, b) <= 0 ? a : b);
export const max = (a: Money, b: Money): Money => (compare(a, b) >= 0 ? a : b);

const NBSP = '\u00A0';

/** Ukrainian display format: "1 234,56 ₴" (non-breaking spaces). */
export function format(a: Money): string {
  const sign = a.kopecks < 0 ? '-' : '';
  const abs = Math.abs(a.kopecks);
  const hryvnias = Math.floor(abs / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const kop = (abs % 100).toString().padStart(2, '0');
  return `${sign}${hryvnias},${kop}${NBSP}₴`;
}

/** Parses "123", "123.4", "123,45", "-5.00" into money without floating point. */
export function parse(text: string, currency: Currency = 'UAH'): Money {
  const match = /^(-)?(\d+)(?:[.,](\d{1,2}))?$/.exec(text.trim().replace(/[\s\u00A0]/g, ''));
  if (!match) throw new RangeError(`not a money amount: "${text}"`);
  const [, minus, whole = '0', fraction = ''] = match;
  const kopecks = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return money(minus ? -kopecks : kopecks, currency);
}
